import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { EMPTY, Observable, Subject } from 'rxjs';

import { AuthenticationService } from '../../services/authentication.service';
import { CentralServerService } from '../../services/central-server.service';
import { ChatEvent, ChatEventType } from '../../types/Chatbot';

const CONNECTION_TIMEOUT_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class ChatApiService {
  private abortController: AbortController | null = null;
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public constructor(
    private readonly centralServerService: CentralServerService,
    private readonly authenticationService: AuthenticationService,
    private readonly httpClient: HttpClient,
  ) {}

  public connect(query: string, role?: string): Observable<ChatEvent> {
    // Disconnect any existing connection first (without affecting new subject)
    this.disconnect();

    const eventSubject = new Subject<ChatEvent>();
    this.abortController = new AbortController();

    const token = this.authenticationService.getLoggedUserToken();
    const user = this.authenticationService.getLoggedUser();
    const baseUrl = this.buildChatUrl();

    const params = new URLSearchParams();
    params.append('query', query);
    if (role) {
      params.append('role', role);
    }
    if (user?.tenantID) {
      params.append('tenantId', user.tenantID);
    }

    const url = `${baseUrl}/chat?${params.toString()}`;

    // Set connection timeout
    this.connectionTimeoutId = setTimeout(() => {
      eventSubject.error(new Error('Connection timeout'));
      this.disconnect();
    }, CONNECTION_TIMEOUT_MS);

    void fetchEventSource(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
      openWhenHidden: true,
      signal: this.abortController.signal,
      onopen: (response): Promise<void> => {
        this.clearConnectionTimeout();
        if (!response.ok) {
          throw new Error(`Connection failed: ${response.status}`);
        }
        return Promise.resolve();
      },
      onmessage: (ev: EventSourceMessage) => {
        if (!ev.event || !ev.data) {
          return;
        }
        try {
          const data = JSON.parse(ev.data);
          eventSubject.next({
            type: ev.event as ChatEventType,
            data,
          });
        } catch {
          console.error('Failed to parse SSE event');
        }
      },
      onerror: (err) => {
        this.clearConnectionTimeout();
        const error = err instanceof Error ? err : new Error(String(err) || 'Connection error');
        eventSubject.error(error);
        this.disconnect();
        // Throw to prevent fetchEventSource from automatically retrying
        throw error;
      },
      onclose: () => {
        this.clearConnectionTimeout();
        eventSubject.complete();
      },
    });

    return eventSubject.asObservable();
  }

  public disconnect(): void {
    this.clearConnectionTimeout();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  public reportError(details?: string): Observable<void> {
    const baseUrl = this.buildChatUrl();
    if (!baseUrl) {
      return EMPTY;
    }

    return this.httpClient.post<void>(`${baseUrl}/error-report`, { details });
  }

  public deleteConversation(): Observable<void> {
    const baseUrl = this.buildChatUrl();
    if (!baseUrl) {
      return EMPTY;
    }

    return this.httpClient.delete<void>(`${baseUrl}/chat/conversation`);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
  }

  private buildChatUrl(): string {
    const baseUrl = this.centralServerService.centralRestServerServiceBaseURL;
    if (baseUrl) {
      return `${baseUrl}/v2/api/vev-mcp`;
    }
    return '';
  }
}
